import { useEffect, useState } from "react";
import { api } from "./lib/api.ts";
import { LimitSheet } from "./components/LimitSheet.tsx";
import { Home } from "./screens/Home.tsx";
import { Onboarding, onboardingSkipped, skipOnboarding } from "./screens/Onboarding.tsx";
import { Capital } from "./screens/Capital.tsx";
import { FundsScreen } from "./screens/FundsScreen.tsx";
import { GoalsScreen } from "./screens/GoalsScreen.tsx";
import { BugsScreen } from "./screens/BugsScreen.tsx";
import { CategoriesScreen } from "./screens/CategoriesScreen.tsx";
import { PinsScreen } from "./screens/PinsScreen.tsx";
import { RatesScreen } from "./screens/RatesScreen.tsx";
import { Savings } from "./screens/Savings.tsx";
import { SettingsScreen } from "./screens/SettingsScreen.tsx";
import { Subscriptions } from "./screens/Subscriptions.tsx";
import { TripsScreen } from "./screens/TripsScreen.tsx";
import { Operations } from "./screens/Operations.tsx";
import { AskScreen } from "./screens/AskScreen.tsx";
import { Boundary } from "./components/Boundary.tsx";
import { TAB_BAR_HEIGHT, TabBar, type Tab } from "./components/TabBar.tsx";

/** Вложенные страницы. Их мало и глубина ровно одна — таб → страница, — поэтому
 *  роутера здесь нет: он добавил бы зависимость и адресную строку, которой в
 *  Mini App всё равно не видно. */
type Page = "subscriptions" | "goals" | "funds" | "savings" | "settings" | "rates" | "categories" | "trips" | "pins" | "bugs";

export function App() {
  const [tab, setTab] = useState<Tab>("home");
  const [page, setPage] = useState<Page | null>(null);
  // null — ещё не знаем, показывать ли знакомство. Пока не знаем, не показываем
  // ничего: мигнуть онбордингом человеку, который тут давно, хуже, чем секунда
  // пустого экрана.
  const [greet, setGreet] = useState<boolean | null>(null);
  const [limitSheet, setLimitSheet] = useState(false);

  useEffect(() => {
    if (onboardingSkipped()) { setGreet(false); return; }
    api.me()
      .then((me) => setGreet(me.monthly_allowance === null))
      .catch(() => setGreet(false));   // не узнали — не мешаем работать
  }, []);

  if (greet === null) return null;
  if (greet) {
    return (
      <>
        <Onboarding onSetLimit={() => setLimitSheet(true)}
                    onSkip={() => { skipOnboarding(); setGreet(false); }} />
        {limitSheet && (
          <LimitSheet state={null} onClose={() => setLimitSheet(false)}
                      onDone={() => { setLimitSheet(false); setGreet(false); }} />
        )}
      </>
    );
  }

  const openTab = (next: Tab) => {
    setPage(null);   // ушёл в другой таб — вложенная страница закрывается
    setTab(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      {/* Отступ снизу — место под закреплённую панель: без него последняя
          строка любого списка прячется под ней. */}
      <main style={{ flexGrow: 1, paddingBottom: TAB_BAR_HEIGHT }}>
        <Boundary key={`${tab}:${page}`}>
        {page === "subscriptions" ? (
          <Subscriptions onBack={() => setPage(null)} />
        ) : page === "funds" ? (
          <FundsScreen onBack={() => setPage(null)} />
        ) : page === "goals" ? (
          <GoalsScreen onBack={() => setPage(null)} />
        ) : page === "savings" ? (
          <Savings onBack={() => setPage(null)} />
        ) : page === "settings" ? (
          <SettingsScreen onBack={() => setPage(null)} onOpen={setPage} />
        ) : page === "rates" ? (
          <RatesScreen onBack={() => setPage("settings")} />
        ) : page === "categories" ? (
          <CategoriesScreen onBack={() => setPage("settings")} />
        ) : page === "trips" ? (
          <TripsScreen onBack={() => setPage("settings")} />
        ) : page === "pins" ? (
          <PinsScreen onBack={() => setPage("settings")} />
        ) : page === "bugs" ? (
          <BugsScreen onBack={() => setPage("settings")} />
        ) : tab === "home" ? (
          <Home onOpen={setPage} onTab={openTab} />
        ) : tab === "ops" ? (
          <Operations />
        ) : tab === "ask" ? (
          <AskScreen />
        ) : (
          <Capital onOpen={setPage} />
        )}
        </Boundary>
      </main>
      <TabBar active={tab} onChange={openTab} />
    </div>
  );
}
